import { useQuery } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../data/constants';
import { getLumberItemInfoBySchedule } from '../../api/ebs-mfg-api/index.js';
import { useApiReady } from '@pella/fetch-client-react';

/**
 * Hook to fetch lumber item info from ebs-mfg given a valid schedule number.
 * Returns a normalized shape: { data, isPending, error } where error is
 * a human-readable message string covering both API failures and soft
 * not-found cases (no schedule match on the response).
 * @param {object} input
 * @param {string | undefined} input.scheduleNumber
 * @param {boolean} [input.enabled] - Optional override; when false the query is disabled regardless of other params
 */
const useLumberScheduleLookup = ({ scheduleNumber, enabled: enabledOverride = true }) => {
  const { apiReady } = useApiReady();
  const query = useQuery({
    queryKey: [QUERY_KEYS.LUMBER_ITEM_INFO_BY_SCHEDULE, scheduleNumber],
    queryFn: () =>
      getLumberItemInfoBySchedule({
        scheduleNumber: /** @type {string} */ (scheduleNumber),
      }),
    enabled: Boolean(enabledOverride && apiReady && scheduleNumber),
  });

  const error =
    query.error?.message ||
    (query.isSuccess && !query.data?.scheduleNumber
      ? 'Invalid schedule number. No details found for the entered schedule number.'
      : null) ||
    null;

  return {
    data: query.data,
    isPending: query.isPending,
    error,
  };
};

export { useLumberScheduleLookup };
